import React, {
  ComponentType,
  useContext,
  useEffect
} from 'react';
import { ELSPropsFromToastService } from '@els/els-component-toast-react';
import {
  fetchAssessmentByIdAction,
  fetchEolsAssessmentSubmissionsAction,
  fetchQuizSessionByRecommendationAttemptIdAction,
  fetchRemediationAssignmentWithSourcesAction,
  fetchRecommendationAttemptsAction,
  fetchRaasAssessmentAction,
  fetchRecommendationItemsAction,
  fetchRecommendationsAction,
  fetchAssessmentMetricsAction,
  fetchRaasAssessmentSourceAction,
  fetchEvolveUsersAction,
  fetchEolsUserAction,
  fetchRecommendationSourcesAction,
  fetchHesiExamInfoByExamEditionIdAction,
  trackNewPageLoadAction,
  updateRemediationBaseState
} from '../../components/app/app-state-actions.utilities';
import { AppContext } from '../../components/app/AppProvider.component';
import {
  QuizAttemptDetailsDto,
  RecommendationAttemptDto,
  RecommendationContentTypeDto,
  RecommendationDto,
  RaasAssessmentDto,
  RecommendationItemDto,
  RemediationAssignmentWithSourcesDto,
  RecommendationGenerationStatusDto,
  RaasAssessmentSourceDto,
  RaasAssessmentMetricsDto,
  RecommendationSourceDto,
  HesiExamInfoDto,
  SherpathAIContextDto
} from '../../apis/nhe-remediation-service/nhe-remediation-service.dtos';
import {
  getAssessmentIdForInit,
  getQuizAttempt,
  getHesiEditionId
} from '../../utilities/remediation.utilities';
import {
  AssessmentSubmissionDto,
  EolsAssessmentDto
} from '../../apis/eols-assessment-service/eols-assessment-service.dtos';

type ComponentProps = ELSPropsFromToastService;

export type RemediationBaseState = {
  remediationAssignmentWithSources: RemediationAssignmentWithSourcesDto;
  eolsAssessment: EolsAssessmentDto;
  raasAssessment: RaasAssessmentDto;
  raasAssessmentSourceList: RaasAssessmentSourceDto[];
  raasAssessmentSubmissions: AssessmentSubmissionDto[];
  recommendations: RecommendationDto[];
  recommendationItems: RecommendationItemDto[];
  recommendationAttempts: RecommendationAttemptDto[];
  recommendationSources: RecommendationSourceDto[];
  quizAttemptDetails: QuizAttemptDetailsDto;
  attemptAssessmentSubmissions: AssessmentSubmissionDto[];
  showPendingAssessmentMessage: boolean;
  assessmentMetrics: RaasAssessmentMetricsDto;
  hesiExamInfo: HesiExamInfoDto;
  sherpathAIContext: SherpathAIContextDto;
}

// Empty type - components no longer receive baseState as a prop
export type RemediationBaseStateProps = Record<string, never>;

const withRemediationBase = (useCache = false) => (WrappedComponent: ComponentType<ComponentProps>): ComponentType<ComponentProps> => {
  // eslint-disable-next-line sonarjs/cognitive-complexity
  const RemediationBase = (props: ComponentProps) => {
    const {
      appState,
      setAppState,
      appStateRef
    } = useContext(AppContext);

    const initFromApis = async (assessmentId: number) => {
      try {
        const [raasAssessment, recommendations, assessmentMetrics] = await Promise.all([
          fetchRaasAssessmentAction({
            appState,
            appStateRef,
            setAppState,
            id: null,
            studentStudyEolsAssessmentId: assessmentId,
            useCache: true
          }),
          fetchRecommendationsAction({
            appState,
            appStateRef,
            setAppState,
            assessmentId,
            recommendationIds: null,
            useCache: true
          }),
          fetchAssessmentMetricsAction({
            appState,
            appStateRef,
            setAppState,
            assessmentId,
            useCache
          })
        ]);

        const eolsAssessment = raasAssessment
          ? await fetchAssessmentByIdAction({
            appState,
            appStateRef,
            setAppState,
            userId: raasAssessment.userId.toString(),
            assessmentId,
            useCache: true
          })
          : null;

        if (!raasAssessment || !raasAssessment.remediationAssignmentId) {
          updateRemediationBaseState(setAppState, {
            raasAssessment,
            eolsAssessment,
            recommendations,
            assessmentMetrics,
            remediationAssignmentWithSources: null,
            showPendingAssessmentMessage: true
          });
          return;
        }

        // Fetch secondary data in parallel - actions will use cache if flag is set
        const secondaryFetchPromises = [];

        // raasAssessmentSourceList
        secondaryFetchPromises.push(fetchRaasAssessmentSourceAction({
          appState,
          appStateRef,
          setAppState,
          raasAssessmentId: raasAssessment.id,
          useCache: true
        }));

        // raasAssessmentSubmissions
        secondaryFetchPromises.push(fetchEolsAssessmentSubmissionsAction({
          setAppState,
          appState,
          appStateRef,
          assessmentIds: [raasAssessment.studentStudyEolsAssessmentId],
          useCache,
          stateKey: 'raasAssessmentSubmissions'
        }));

        // remediationAssignmentWithSources (always use cache for this static data)
        secondaryFetchPromises.push(fetchRemediationAssignmentWithSourcesAction({
          appState,
          appStateRef,
          setAppState,
          useCache: true,
          remediationAssignmentId: raasAssessment.remediationAssignmentId
        }));

        const [raasAssessmentSourceList, raasAssessmentSubmissions, remediationAssignmentWithSources] = await Promise.all(secondaryFetchPromises);

        // Fetch HESI exam info if it's a HESI source - action will use cache if flag is set
        const editionId = getHesiEditionId(remediationAssignmentWithSources as RemediationAssignmentWithSourcesDto);
        let hesiExamInfo: HesiExamInfoDto = null;
        if (editionId) {
          hesiExamInfo = await fetchHesiExamInfoByExamEditionIdAction({
            appState,
            appStateRef,
            setAppState,
            editionId,
            useCache: true
          });
        }

        const showPendingAssessmentMessage =
          !recommendations ||
          !recommendations.length ||
          !raasAssessment ||
          raasAssessment.recGenerationStatus !== RecommendationGenerationStatusDto.COMPLETED;

        // Update app state with main data
        updateRemediationBaseState(setAppState, {
          eolsAssessment,
          remediationAssignmentWithSources: remediationAssignmentWithSources as RemediationAssignmentWithSourcesDto,
          recommendations,
          raasAssessment,
          raasAssessmentSourceList,
          raasAssessmentSubmissions,
          showPendingAssessmentMessage,
          assessmentMetrics,
          hesiExamInfo
        });

        if (showPendingAssessmentMessage) {
          // No need to make more requests if no recommendations
          return;
        }

        // Fetch recommendation-related data - actions will use cache if flag is set
        const recIds = recommendations.map((rec) => rec.id);

        // Fetch items for quiz recommendations
        const itemsArrays = await Promise.all(recommendations.map((recommendation) => {
          if (recommendation.contentType !== RecommendationContentTypeDto.RAAS_QUIZ) {
            return Promise.resolve(null as RecommendationItemDto[]);
          }
          return fetchRecommendationItemsAction({
            appState,
            appStateRef,
            setAppState,
            recommendationId: recommendation.id,
            useCache: true
          });
        }));
        const recommendationItems = itemsArrays.reduce((acc, cur) => [...acc, ...(cur || [])], []);

        // Fetch recommendation attempts and sources in parallel
        const [attempts, recommendationSources] = await Promise.all([
          fetchRecommendationAttemptsAction({
            appState,
            appStateRef,
            setAppState,
            recommendationIds: recIds,
            useCache
          }),
          fetchRecommendationSourcesAction({
            appState,
            appStateRef,
            setAppState,
            recommendationIds: recIds,
            useCache
          })
        ]);
        const recommendationAttempts = attempts.reduce((acc, cur) => (cur ? [...acc, cur] : acc), []);

        // Fetch quiz attempt details if exists
        const quizAttempt = getQuizAttempt(recommendations, recommendationAttempts);
        let quizAttemptDetails = null;

        if (quizAttempt) {
          try {
            quizAttemptDetails = await fetchQuizSessionByRecommendationAttemptIdAction({
              appState,
              appStateRef,
              setAppState,
              recommendationAttemptId: quizAttempt.id,
              useCache
            });
          } catch {
            quizAttemptDetails = null;
          }
        }

        // Fetch assessment submissions for attempts
        const assessmentIds = recommendationAttempts
          .filter((attempt) => attempt.eolsAssessmentId)
          .map((attempt) => attempt.eolsAssessmentId);

        let attemptAssessmentSubmissions = null;
        if (assessmentIds?.length) {
          attemptAssessmentSubmissions = await fetchEolsAssessmentSubmissionsAction({
            appState,
            appStateRef,
            setAppState,
            assessmentIds,
            useCache,
            stateKey: 'attemptAssessmentSubmissions'
          });
        }

        // Final update with all attempt-related data
        updateRemediationBaseState(setAppState, {
          attemptAssessmentSubmissions,
          recommendationItems,
          recommendationAttempts,
          recommendationSources,
          quizAttemptDetails
        });
      } catch (error) {
        // Handle errors by setting error state
        updateRemediationBaseState(setAppState, {
          eolsAssessment: null,
          remediationAssignmentWithSources: null,
          recommendations: [],
          raasAssessment: null,
          showPendingAssessmentMessage: true,
          raasAssessmentSourceList: null
        });
      }
    };

    useEffect(() => {
      const studentStudyEolsAssessmentId = getAssessmentIdForInit(appState);

      // Don't run if we don't have the required data yet
      if (!studentStudyEolsAssessmentId) {
        return;
      }

      Promise.allSettled([
        fetchEvolveUsersAction({
          useCache: true,
          userId: appState.userId,
          appState,
          appStateRef,
          setAppState
        }),
        fetchEolsUserAction({
          useCache: true,
          userId: appState.userId,
          appState,
          appStateRef,
          setAppState
        }),
        initFromApis(studentStudyEolsAssessmentId)
      ]).finally(() => {
        trackNewPageLoadAction({ appState, appStateRef, setAppState });
      });
    }, [appState.userId, appState.appLinkData, appState.selectedRaasAssessmentDto]);

    return (
      <WrappedComponent
        {...props}
      />
    );
  };
  RemediationBase.displayName = 'withRemediationBase';

  return RemediationBase;
};

export default withRemediationBase;
